import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ContactOption } from '@/components/WhatsAppChatWidget';

export interface WidgetConfig {
  websiteName: string;
  displayName: string;
  logoImage: string;
  messagePlaceholder: string;
  welcomeMessage: string;
  responseTimeText: string;
  startConversationTitle: string;
  chatButtonText: string;
  brandColor: string;
  buttonPosition: 'bottom-left' | 'bottom-right';
  contactOptions: ContactOption[];
  brandName: string;
  showPoweredBy: boolean;
  showFloatingButton: boolean;
  enableAnimation: boolean;
  websiteUrl: string;
  channelName: string;
  phoneNumber: string;
  email: string;
  connectedAgent?: string;
  customMessage: string;
  scriptId?: string | null;
  embedScript?: string | null;
}

export const FIXED_WIDGET_TEXT = {
  messagePlaceholder: 'Type your message...',
  welcomeMessage: 'Hi, how can I help you today?',
  responseTimeText: 'We typically reply in a few mins.',
  startConversationTitle: 'Start a conversation',
  chatButtonText: 'Chat with us',
  brandName: 'wenext.ai',
  showPoweredBy: true,
  enableAnimation: true,
};

const sanitizeConfig = (config: WidgetConfig): WidgetConfig => ({
  ...config,
  messagePlaceholder: FIXED_WIDGET_TEXT.messagePlaceholder,
  welcomeMessage: FIXED_WIDGET_TEXT.welcomeMessage,
  responseTimeText: FIXED_WIDGET_TEXT.responseTimeText,
  startConversationTitle: FIXED_WIDGET_TEXT.startConversationTitle,
  chatButtonText: FIXED_WIDGET_TEXT.chatButtonText,
  brandName: FIXED_WIDGET_TEXT.brandName,
  showPoweredBy: FIXED_WIDGET_TEXT.showPoweredBy,
  enableAnimation: FIXED_WIDGET_TEXT.enableAnimation,
  customMessage: config.customMessage || 'Hi! How can I help you?',
});

interface WidgetConfigContextType {
  config: WidgetConfig;
  updateConfig: (newConfig: Partial<WidgetConfig>) => void;
  getGradientColors: (brandColor: string) => { from: string; to: string };
}

const defaultConfig: WidgetConfig = {
  websiteName: 'my website',
  displayName: 'my website Support',
  logoImage: '',
  messagePlaceholder: FIXED_WIDGET_TEXT.messagePlaceholder,
  welcomeMessage: FIXED_WIDGET_TEXT.welcomeMessage,
  responseTimeText: FIXED_WIDGET_TEXT.responseTimeText,
  startConversationTitle: FIXED_WIDGET_TEXT.startConversationTitle,
  chatButtonText: FIXED_WIDGET_TEXT.chatButtonText,
  brandColor: '#434343',
  buttonPosition: 'bottom-right',
  contactOptions: [
    { id: '1', label: 'WhatsApp', icon: '/assets/social-media/whatsapp.png', href: '', isActive: true },
    { id: '2', label: 'Email', icon: '/assets/social-media/communication.png', href: '', isActive: true },
    { id: '3', label: 'Instagram', icon: '/assets/social-media/instagram.png', href: '', isActive: true },
  ],
  brandName: FIXED_WIDGET_TEXT.brandName,
  showPoweredBy: FIXED_WIDGET_TEXT.showPoweredBy,
  showFloatingButton: true,
  enableAnimation: FIXED_WIDGET_TEXT.enableAnimation,
  websiteUrl: '',
  channelName: '',
  phoneNumber: '',
  email: '',
  connectedAgent: '',
  customMessage: 'Hi! How can I help you?',
  scriptId: '',
  embedScript: '',
};

const WidgetConfigContext = createContext<WidgetConfigContextType | undefined>(undefined);

export const WidgetConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<WidgetConfig>(() => {
    // No longer loading from localStorage - API is the source of truth
    return sanitizeConfig(defaultConfig);
  });

  // No longer saving to localStorage - API is the source of truth

  const updateConfig = (newConfig: Partial<WidgetConfig>) => {
    setConfig(prev => sanitizeConfig({ ...prev, ...newConfig }));
  };

  const getGradientColors = (brandColor: string) => {
    const hex = brandColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const brightness = (r + g + b) / 3;
    if (brightness > 240) {
      const darkerR = Math.max(0, r - 100);
      const darkerG = Math.max(0, g - 100);
      const darkerB = Math.max(0, b - 100);
      return {
        from: `rgb(${darkerR}, ${darkerG}, ${darkerB})`,
        to: `rgb(${Math.max(0, darkerR - 40)}, ${Math.max(0, darkerG - 40)}, ${Math.max(0, darkerB - 40)})`,
      };
    }

    const darkerR = Math.max(0, r - 40);
    const darkerG = Math.max(0, g - 40);
    const darkerB = Math.max(0, b - 40);

    return {
      from: `rgb(${r}, ${g}, ${b})`,
      to: `rgb(${darkerR}, ${darkerG}, ${darkerB})`,
    };
  };

  return (
    <WidgetConfigContext.Provider value={{ config, updateConfig, getGradientColors }}>
      {children}
    </WidgetConfigContext.Provider>
  );
};

export const useWidgetConfig = () => {
  const context = useContext(WidgetConfigContext);
  if (context === undefined) {
    throw new Error('useWidgetConfig must be used within a WidgetConfigProvider');
  }
  return context;
};

